// Australian financial-year (FY) and date helpers for the finance section.
//
// An AU FY runs 1 July → 30 June and is identified by the calendar year it
// ENDS in: FY 2026 = 1 Jul 2025 – 30 Jun 2026, labelled "2025–26".
//
// Dates are calendar dates, handled as local Y/M/D components (or
// 'YYYY-MM-DD' strings) — never via toISOString(), which can shift a
// local-midnight date back a day (see AGENTS.md timezone pitfall).

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

function daysInMonth(y, m) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** A Date (local components) or 'YYYY-MM-DD…' string → { y, m, d } (m is 1–12). */
export function toYmd(dateLike) {
  let y, m, d;
  if (dateLike instanceof Date) {
    if (Number.isNaN(dateLike.getTime())) throw new RangeError('Invalid Date');
    [y, m, d] = [dateLike.getFullYear(), dateLike.getMonth() + 1, dateLike.getDate()];
  } else {
    const match = ISO_DATE.exec(String(dateLike ?? ''));
    if (!match) throw new RangeError(`Not a YYYY-MM-DD date: ${dateLike}`);
    [y, m, d] = match.slice(1).map(Number);
  }
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) {
    throw new RangeError(`Invalid calendar date: ${dateLike}`);
  }
  return { y, m, d };
}

/** { y, m, d } (or anything `toYmd` accepts) → 'YYYY-MM-DD'. */
export function isoDate(dateLike) {
  const { y, m, d } =
    dateLike && typeof dateLike === 'object' && !(dateLike instanceof Date)
      ? dateLike
      : toYmd(dateLike);
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** The FY (end year) a date falls in: 2025-07-01 → 2026, 2026-06-30 → 2026. */
export function fyOf(dateLike) {
  const { y, m } = toYmd(dateLike);
  return m >= 7 ? y + 1 : y;
}

/** The FY containing `now` (defaults to today, local time). */
export function currentFy(now = new Date()) {
  return fyOf(now);
}

/** Display label for an FY: 2026 → "2025–26" (en dash). */
export function fyLabel(fy) {
  return `${fy - 1}–${String(fy % 100).padStart(2, '0')}`;
}

/** First day of an FY as 'YYYY-MM-DD': 2026 → '2025-07-01'. */
export function fyStart(fy) {
  return `${fy - 1}-07-01`;
}

/** Last day of an FY as 'YYYY-MM-DD': 2026 → '2026-06-30'. */
export function fyEnd(fy) {
  return `${fy}-06-30`;
}

/**
 * The last calendar-quarter end (31 Mar / 30 Jun / 30 Sep / 31 Dec) strictly
 * before a date, as 'YYYY-MM-DD': 2025-07-01 → '2025-06-30', 2025-06-30 →
 * '2025-03-31'. A fund's quarterly distribution paid on a date belongs to it.
 */
export function quarterEndBefore(dateLike) {
  const { y, m, d } = toYmd(addDays(dateLike, -1));
  if (m % 3 === 0 && d === daysInMonth(y, m)) return isoDate({ y, m, d });
  let qm = m - (m % 3 || 3);
  let qy = y;
  if (qm <= 0) {
    qm += 12;
    qy -= 1;
  }
  return isoDate({ y: qy, m: qm, d: daysInMonth(qy, qm) });
}

/** FY quarter 1–4 (Q1 = Jul–Sep … Q4 = Apr–Jun) — PAYG instalments, super caps. */
export function fyQuarter(dateLike) {
  const { m } = toYmd(dateLike);
  return m >= 7 ? Math.floor((m - 7) / 3) + 1 : Math.floor((m - 1) / 3) + 3;
}

function utcDay(dateLike) {
  const { y, m, d } = toYmd(dateLike);
  return Date.UTC(y, m - 1, d);
}

/** Whole calendar days from `a` to `b` (negative if b is earlier). DST-proof. */
export function daysBetween(a, b) {
  return Math.round((utcDay(b) - utcDay(a)) / 86_400_000);
}

/** Days in a calendar month (m is 1–12). */
export function monthLength(y, m) {
  return daysInMonth(y, m);
}

/** A date `n` days later (negative = earlier), as 'YYYY-MM-DD'. */
export function addDays(dateLike, n) {
  const t = new Date(utcDay(dateLike) + n * 86_400_000);
  return isoDate({ y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() });
}

/**
 * The same day-of-month `n` months later (negative = earlier), clamped to the
 * month's last day: 2025-01-31 + 1 → 2025-02-28. `day` overrides the day.
 */
export function addMonths(dateLike, n, day) {
  const { y, m, d } = toYmd(dateLike);
  const index = y * 12 + (m - 1) + n;
  const ny = Math.floor(index / 12);
  const nm = (index % 12) + 1;
  return isoDate({ y: ny, m: nm, d: Math.min(day ?? d, daysInMonth(ny, nm)) });
}

/**
 * Whether a CGT asset qualifies for the 50% general discount: owned for at
 * least 12 months, EXCLUDING the acquisition day and the disposal (CGT event)
 * day. So the disposal must fall strictly AFTER the first anniversary of
 * acquisition — bought 15 Mar 2024 → eligible from 16 Mar 2025. A 29 Feb
 * acquisition's anniversary is 28 Feb (eligible from 1 Mar).
 * (For RSUs/ESS shares the CGT acquisition date is the VEST date.)
 */
export function cgtDiscountEligible(acquired, disposed) {
  const { y, m, d } = toYmd(acquired);
  const anniversary = { y: y + 1, m, d: Math.min(d, daysInMonth(y + 1, m)) };
  return daysBetween(isoDate(anniversary), disposed) > 0;
}
